import React from "react";
import { withTranslation } from "react-i18next";
import { FormGroup, FormLabel, FormControl } from "react-bootstrap";
import { toastr } from "react-redux-toastr";
import { FiCopy, FiEye } from "react-icons/fi";
import moment from "moment";
import Loading from "components/Loading";
import Switch from "components/Switch";
import InlineAlert from "components/InlineAlert";
import PayflowErrorLogModal from "components/Integrations/PayflowErrorLogModal";
import { payflowIntegrationService } from "services";
import { errorFormating } from "utils/utils";
import payflowLogo from "assets/payflow_logo.png";

class PayflowIntegrationCard extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			saving: false,
			payflow_active: false,
			payflow_api_key: "",
			storedApiKey: "",
			apiKeyDirty: false,
			lastSyncAt: null,
			nextSyncAt: null,
			syncError: null,
		};
	}

	componentDidMount() {
		this.loadIntegrations();
	}

	parseSyncLogs = (logsResponse) => {
		const logs = Array.isArray(logsResponse?.data)
			? logsResponse.data
			: Array.isArray(logsResponse)
				? logsResponse
				: [];

		const sortedLogs = [...logs].sort(
			(a, b) => new Date(b.created || 0) - new Date(a.created || 0)
		);
		const latestSuccess = sortedLogs.find((log) => {
			const type = String(log.type || "").toUpperCase();
			return type === "CREATED" || type === "UPDATED" || type === "SUCCESS";
		});
		const latestError = sortedLogs.find(
			(log) => String(log.type || "").toUpperCase() === "ERROR"
		);

		return {
			lastSyncAt: latestSuccess?.created || null,
			syncError: latestError?.description || null,
		};
	};

	loadIntegrations = async () => {
		this.setState({ loading: true });
		const [integrationsResponse, logsResponse] = await Promise.all([
			payflowIntegrationService.getIntegrations(),
			payflowIntegrationService.getSyncLogs(),
		]);

		if (!integrationsResponse || integrationsResponse.ok === false) {
			this.setState({ loading: false });
			return;
		}

		const data = integrationsResponse.data || integrationsResponse;
		const { lastSyncAt: lastSuccessSyncAt, syncError: latestLogError } =
			this.parseSyncLogs(logsResponse);

		this.setState({
			loading: false,
			payflow_active: Boolean(data.payflow_active),
			payflow_api_key: data.payflow_api_key || "",
			storedApiKey: data.payflow_api_key || "",
			apiKeyDirty: false,
			lastSyncAt:
				data.payflow_last_sync_at ||
				data.payflow_last_sync ||
				data.last_sync_at ||
				lastSuccessSyncAt,
			nextSyncAt:
				data.payflow_next_sync_at ||
				data.payflow_next_sync ||
				data.next_sync_at ||
				null,
			syncError:
				data.payflow_sync_error ||
				data.payflow_last_error ||
				data.sync_error ||
				latestLogError,
		});
	};

	saveIntegrations = async (overrides = {}) => {
		const { t } = this.props;
		const {
			saving,
			payflow_active,
			payflow_api_key,
			storedApiKey,
			apiKeyDirty,
		} = this.state;
		if (saving) return false;

		const nextActive =
			overrides.payflow_active !== undefined
				? overrides.payflow_active
				: payflow_active;
		const nextApiKey =
			overrides.payflow_api_key !== undefined
				? overrides.payflow_api_key
				: payflow_api_key;

		if (nextActive && !nextApiKey && !storedApiKey) {
			toastr.warning(
				t("integrations.payflow.apiKeyRequiredTitle"),
				t("integrations.payflow.apiKeyRequiredMessage")
			);
			return false;
		}

		this.setState({ saving: true });

		const payload = {
			payflow_active: nextActive,
			payflow_api_key: apiKeyDirty || !storedApiKey ? nextApiKey : storedApiKey,
		};

		const response = await payflowIntegrationService.updateIntegrations(payload);

		if (!response || response.ok === false) {
			const errs = errorFormating(response);
			toastr.error(t("common.error"), errs.general || t("common.errorSaving"));
			this.setState({ saving: false });
			return false;
		}

		toastr.success(t("common.success"), t("common.changesSaved"));
		await this.loadIntegrations();
		this.setState({ saving: false, apiKeyDirty: false });
		return true;
	};

	handleToggle = async () => {
		const nextActive = !this.state.payflow_active;
		this.setState({ payflow_active: nextActive });
		const saved = await this.saveIntegrations({ payflow_active: nextActive });
		if (!saved) {
			this.setState({ payflow_active: !nextActive });
		}
	};

	handleApiKeyChange = (event) => {
		this.setState({
			payflow_api_key: event.target.value,
			apiKeyDirty: true,
		});
	};

	copyApiKey = async () => {
		const { t } = this.props;
		const { payflow_api_key } = this.state;
		if (!payflow_api_key) return;

		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(payflow_api_key);
				toastr.success(t("common.success"), t("integrations.payflow.apiKeyCopied"));
			}
		} catch (_) {
			toastr.error(t("common.error"), t("integrations.payflow.apiKeyCopyError"));
		}
	};

	openLogsModal = () => {
		const { t, showModal, hideModal } = this.props;
		showModal(
			{
				open: true,
				title: t("integrations.payflow.logs.title"),
				style: { width: "720px" },
				content: (
					<PayflowErrorLogModal handleClose={() => hideModal()} />
				),
				closeModal: () => hideModal(),
			},
			"edit"
		);
	};

	formatSyncDate = (value) => {
		if (!value) return "—";
		const m = moment.utc(value).local();
		return m.isValid() ? m.format("D MMMM YYYY - HH:mm") : "—";
	};

	render() {
		const { t } = this.props;
		const {
			loading,
			saving,
			payflow_active,
			payflow_api_key,
			lastSyncAt,
			nextSyncAt,
			syncError,
		} = this.state;

		if (loading) {
			return (
				<article className="integration-card">
					<Loading />
				</article>
			);
		}

		return (
			<article className="integration-card">
				<header className="integration-card__header">
					<div className="integration-card__brand">
						<img
							src={payflowLogo}
							alt="Payflow"
							className="integration-card__logo"
						/>
						<h3 className="integration-card__title">Payflow</h3>
					</div>
					<div className="integration-card__toggle">
						<span className="integration-card__toggle-label">
							{payflow_active
								? t("integrations.payflow.active")
								: t("integrations.payflow.inactive")}
						</span>
						<Switch
							controlId="payflow_active"
							isOn={payflow_active}
							handleToggle={this.handleToggle}
							disabled={saving}
						/>
					</div>
				</header>

				<p className="integration-card__description">
					{t("integrations.payflow.description")}
				</p>

				<FormGroup className="form-group integration-card__api-key" controlId="payflow_api_key">
					<FormLabel>{t("integrations.payflow.apiKeyLabel")}</FormLabel>
					<div className="integration-card__api-key-input">
						<FormControl
							type="text"
							value={payflow_api_key}
							onChange={this.handleApiKeyChange}
							placeholder={t("integrations.payflow.apiKeyPlaceholder")}
							className="form-control-modern"
							disabled={saving}
							autoComplete="off"
						/>
						{payflow_api_key && (
							<button
								type="button"
								className="btn btn-transparent integration-card__copy-btn"
								onClick={this.copyApiKey}
								title={t("integrations.payflow.copyApiKey")}
								aria-label={t("integrations.payflow.copyApiKey")}
							>
								<FiCopy />
							</button>
						)}
					</div>
				</FormGroup>

				{syncError && (
					<InlineAlert variant="error" layout="banner" className="integration-card__alert">
						{syncError}
					</InlineAlert>
				)}

				<footer className="integration-card__footer">
					<div className="integration-card__sync-meta">
						<div>
							<span className="integration-card__meta-label">
								{t("integrations.payflow.lastSync")}
							</span>
							<span className="integration-card__meta-value">
								{this.formatSyncDate(lastSyncAt)}
							</span>
						</div>
						<div>
							<span className="integration-card__meta-label">
								{t("integrations.payflow.nextSync")}
							</span>
							<span className="integration-card__meta-value">
								{this.formatSyncDate(nextSyncAt)}
							</span>
						</div>
					</div>
					<button
						type="button"
						className="btn btn-outline-primary integration-card__logs-btn"
						onClick={this.openLogsModal}
					>
						<FiEye />
						{t("integrations.payflow.logs.button")}
					</button>
				</footer>
			</article>
		);
	}
}

export default withTranslation()(PayflowIntegrationCard);
